import React, { useEffect, useState } from "react";
import {
    X as CloseIcon,
    DollarSign,
    MapPin,
    Globe,
    Shield,
    Clock,
    Briefcase,
    TextSelectionIcon,
    LogOut,
    Wallet,
    Bell,
    FileCheck,
    MessageCircle,
    StarIcon,
    SettingsIcon,
    UserIcon,
    User2Icon,
    Lock,
    Verified,
    FileTextIcon,
    Home,
    Search,
    Plus,
    ChartAreaIcon,
    FileText,
    User,
    User2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../../../components/LanguageSelector";
import { getTranslatedCategoryName, getTranslatedSubcategories } from "../../../utils/categoryTranslation";

import { createUserJobs, getTypeCategories, LogoutManually, LogoutUser, useAppDispatch, createUserCategories, getJobById, updateUserJob } from "../../../redux";
import { GlobalService } from "../../../modules";
import { useSelector } from "react-redux";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { getEmergencyProfessionals } from "../../../redux/Actions/AdminAction";
import { assignProfessionalWhatsApp } from "../../../redux/Actions/AuthAction";
import { createQuote } from "../../../redux/Actions/QuoteActions";
import { createUserNotificationAction } from "../../../redux/Actions/UserNotificationActions";
import { QuoteStatusEnum, PurchaseQuoteTypeEnum } from "../../shared/model/quote.enum";
import { NotificationStatusEnum, NotificationPriorityEnum, NotificationTypeEnum } from "../../shared/model/user-notification.enum";
import { JobStatusEnum } from "../../shared/model/job-status.enum";
import { getQuotePricingsByCategory } from "../../../redux/Actions/AdminQuotePricingActions";
import { calculateQuotePrice, type QuotePriceResult } from "../../../utils/quotePricingUtils";
import type { QuotePricingModel } from "../../shared/model/admin-quote-pricing.interfaces";
import Card from "../../../components/ui/card";
import Button from "../../../components/ui/Button";
import SharedFooter3 from "../../../Footer/SharedFooter3";
import SharedHeader5 from "../../../Headers/SharedHeader5";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Spinner from "../../shared/components/Spinner";
import { TypeEnum, RoleEnum } from "../../shared/model";
import ProfileCompletionDialog from "../../../components/ui/ProfileCompletionDialog";
import VerificationRequirementDialog from "../../../components/VerificationRequirementDialog";
import { useVerification } from "../../../hooks/useVerification";
import PlumberRequestForm from "../../../pages/Main/PlumberRequestForm";
import { MdReviews } from "react-icons/md";
import LawyerRequestForm from "../../../pages/Main/LawyerRequestForm";
import SocialMediaLinks from "../../../components/ui/SocialMediaLinks";

const PostJobPage = ({
    jobType = "local",
    showSidebar = true,
    editJobId,
    onJobUpdated
}: {
    jobType?: "local" | "international",
    showSidebar?: boolean,
    editJobId?: { jobId: string },
    onJobUpdated?: () => void
}) => {
    const { t, i18n } = useTranslation();
    const currentLang = i18n.language.split("-")[0];
    const { jobId } = editJobId?.jobId ? editJobId : useParams();
    const [step, setStep] = useState(1);
    const { user, currentJob } = useSelector((state: any) => state.auth);
    const dispatch = useAppDispatch();
    const { typeCategories } = useSelector((state: any) => state.client);
    const isLoading = useSelector(selectLoaderValue);
    const isEditMode = !!jobId;
    const [errors, setErrors] = useState<any>({});
    const [formData, setFormData] = useState({
        requestType: "", // 'quote' or 'hire'
        serviceLocation: jobType === "international" ? TypeEnum.International_Service : 0, // Set default based on jobType
        urgency: "normal", // 'normal' or 'emergency'
        category: "",
        selectedSubcategories: [] as string[], // Add selected subcategories
        title: "",
        description: "",
        budget: "",
        timeline: "",
        location: {
            address: "",
            city: "",
            province: "",
            municipality: "",
            postcode: "",
        },
    });
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [collectedFormData, setCollectedFormData] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [showProfileCompletionDialog, setShowProfileCompletionDialog] = useState(false);
    const [showVerificationDialog, setShowVerificationDialog] = useState(false);
    
    // Use verification hook
    const { isVerified, goToVerification, verificationData } = useVerification();
    
    // Track if verification data has been loaded at least once
    const [verificationDataLoaded, setVerificationDataLoaded] = useState(false);
    const [newCategory, setNewCategory] = useState("");
    const [newSubcategories, setNewSubcategories] = useState("");
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);
    const [subcategoryDisplayLimit, setSubcategoryDisplayLimit] = useState(3);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const navigate = useNavigate();
    const mainContentRef = React.useRef<HTMLDivElement>(null);
    const [quotePricings, setQuotePricings] = useState<QuotePricingModel[]>([]);

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
            navigate("/auth/login");
        } catch (error) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        }
    };
    const sidebarClientItems = [
        {
            icon: <Home className="h-5 w-5" />,
            label: t("postJob.dashboard"),
            path: "/clients",
            align: "center",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("postJob.postJobLocal"),
            path: "/clients/postajob",
            align: "center",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("postJob.postJobInternational"),
            path: "/clients/postajob/international",
            align: "left",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("postJob.findLocalProfessionals"),
            path: "/local-professionals",
            align: "center",
        },
        {
            icon: <FileText className="h-5 w-5" />,
            label: t("postJob.findInternationalProfessionals"),
            path: "/professional-international",
            align: "center",
        },

        {
            icon: <User2 className="h-5 w-5" />,
            label: t("postJob.myProfile"),
            path: "/client/profile",
            align: "left",
        },
        {
            icon: <User className="h-5 w-5" />,
            label: t("postJob.profileSettings"),
            path: "/client/profilesettings",
            align: "left",
        },
        // {
        //   icon: <Settings2 className="h-5 w-5" />,
        //   label: "My Settings",
        //   path: "/client/settings",
        //   align: "left",
        // },
        {
            icon: <FileCheck className="h-5 w-5" />,
            label: t("postJob.myOrders"),
            path: "/client/myorders",
            align: "left",
        },
        {
            icon: <Bell className="h-5 w-5" />,
            label: t("postJob.myOffers"),
            path: "/client/myoffers",
            align: "left",
        },
        {
            icon: <StarIcon className="h-5 w-5" />,
            label: t("postJob.favorites"),
            path: "/client/favorites",
            align: "left",
        },
        {
            icon: <MessageCircle className="h-5 w-5" />,
            label: t("postJob.messageOrVideoCall"),
            path: "/client/MessageAndVideoCall",
            align: "left",
        },
        {
            icon: <User className="h-5 w-5" />,
            label: t("postJob.report"),
            path: "/client/reports",
            align: "left",
        },
        {
            icon: <Wallet className="h-5 w-5" />,
            label: t("postJob.wallet"),
            path: "/client/Wallet",
            align: "left",
        },
        {
            icon: <ChartAreaIcon className="h-5 w-5" />,
            label: t("postJob.progress"),
            path: "/client/progress",
            align: "left",
        },
        {
            icon: <MdReviews className="h-5 w-5" />,
            label: t("postJob.reviews"),
            path: "/client/reviews",
            align: "left",
        },
        {
            icon: <Shield className="h-5 w-5" />,
            label: t("postJob.supportTickets"),
            path: "/client/support",
            align: "left",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("postJob.contactUs"),
            path: "/menu/contact",
            align: "center",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("postJob.refundRequest"),
            path: "/verify",
            align: "center",
        },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("postJob.myInvoices"),
            path: "/client/invoices",
            align: "center",
          },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("postJob.verifyAccount"),
            path: "/verify",
            align: "center",
        },
        { icon: <Lock className="h-5 w-5" />, label: t("postJob.changePassword"), path: "/changepassword", align: "center" },
        {
            icon: <LogOut className="h-5 w-5" />,
            label: t("postJob.logOut"),
            path: "/logout",
            align: "left",
        },
    ];


    // Fetch job data for editing
    useEffect(() => {
        if (isEditMode && jobId) {
            const fetchJobData = async () => {
                try {
                    const response = await dispatch(getJobById({ jobId, isShowLoader: false }));
                    if (response.meta.requestStatus === "fulfilled") {
                        const jobData = response.payload;
                        // Populate form data with job data
                        setFormData({
                            requestType: jobData.isQuote ? "quote" : "hire",
                            serviceLocation: Number(jobData.categoryId?.type?.id) || TypeEnum.Home_Service,
                            urgency: jobData.isEmergency ? "emergency" : "normal",
                            category: jobData.categoryId?.id || "",
                            selectedSubcategories: jobData.subCategories || [],
                            title: jobData.title || "",
                            description: jobData.description || "",
                            budget: jobData.budget?.toString() || "",
                            timeline: jobData.deadlineType?.id?.toString() || "",
                            location: {
                                address: jobData.address || "",
                                city: jobData.city || "",
                                province: jobData.province || "",
                                municipality: jobData.municipality || "",
                                postcode: jobData.postalCode?.toString() || "",
                            },
                        });

                        // Fetch categories for the service location
                        if (jobData.categoryId?.type?.id) {
                            await dispatch(getTypeCategories({ id: jobData.categoryId.type.id }));
                        }

                        // Skip to step 4 (PlumberRequestForm) in edit mode
                        setStep(4);
                    }
                } catch (error) {
                    console.error("Error fetching job data:", error);
                    GlobalService.showToast("error", "Error", t("postJob.failedToLoadJobData"));
                }
            };
            fetchJobData();
        }
    }, [isEditMode, jobId, dispatch]);

    // Fetch categories for international jobs when component mounts
    useEffect(() => {
        if (jobType === "international" && !isEditMode) {
            dispatch(getTypeCategories({ id: TypeEnum.International_Service }));
        }
    }, [jobType, isEditMode, dispatch]);

    useEffect(() => {
        if (collectedFormData) {
            handleJobCreation();
        }
    }, [collectedFormData]);

    // Filter categories based on search term (including translated names and subcategories)
    useEffect(() => {
        if (searchTerm.trim() === "" || searchTerm.length < 3) {
            setFilteredCategories([]);
        } else {
            const query = searchTerm.toLowerCase();
            const filtered = typeCategories.filter(category => {
                // Check if category name matches (including translated name)
                const translatedName = getTranslatedCategoryName(category, currentLang);
                const categoryMatches = category.name?.toLowerCase().includes(query) || 
                                       translatedName.toLowerCase().includes(query);

                // Check if any subcategory matches (including translated subcategories)
                const translatedSubcats = getTranslatedSubcategories(category, currentLang) || [];
                const subcategoryMatches = (category.subcategories && category.subcategories.some(sub =>
                    sub.toLowerCase().includes(query)
                )) || (Array.isArray(translatedSubcats) && translatedSubcats.some(sub => sub.toLowerCase().includes(query)));

                return categoryMatches || subcategoryMatches;
            });
            setFilteredCategories(filtered);
        }
    }, [searchTerm, typeCategories, currentLang]);


    // Scroll to top when step changes
    useEffect(() => {
        // Scroll both window and main content container to top
        window.scrollTo(0, 0);
        if (mainContentRef.current) {
            mainContentRef.current.scrollTop = 0;
        }
    }, [step]);

    // Fetch quote pricings when category changes
    useEffect(() => {
        if (formData.category) {
            const fetchQuotePricings = async () => {
                try {
                    console.log("Fetching quote pricings for category:", formData.category);
                    const pricings = await getQuotePricingsByCategory(formData.category);
                    setQuotePricings(pricings);
                    console.log("Quote pricings loaded:", pricings);
                } catch (error) {
                    console.error("Error fetching quote pricings:", error);
                    setQuotePricings([]);
                }
            };
            fetchQuotePricings();
        } else {
            setQuotePricings([]);
        }
    }, [formData.category]);

    // Handle going back to step 1 from thank you page
    const handleBackToStep1 = () => {
        setShowThankYou(false);
        setStep(1);
        setCollectedFormData(null);
        setSubcategoryDisplayLimit(3); // Reset subcategory display limit
        setFormData({
            requestType: "",
            serviceLocation: jobType === "international" ? TypeEnum.International_Service : 0,
            urgency: "normal",
            category: "",
            selectedSubcategories: [],
            title: "",
            description: "",
            budget: "",
            timeline: "",
            location: {
                address: "",
                city: "",
                province: "",
                municipality: "",
                postcode: "",
            },
        });
    };

    // Handle adding new category
    const handleAddCategory = async () => {
        if (!newCategory.trim()) return;

        try {
            setIsAddingCategory(true);

            // Create the new category object with proper backend structure
            const categoryData = {
                name: newCategory.trim(),
                subcategories: newSubcategories.trim() ? newSubcategories.split(",").map(s => s.trim()) : [],
                type: {
                    id: Number(formData.serviceLocation || TypeEnum.Home_Service)
                },
                description: "", // Required field
                requirements: [], // Required field
                isEmergency: false, // Required field
                isRequireVerification: false, // Required field
                translatedName: {},
                translatedSubCategories: {}
            };

            console.log("Adding new category:", categoryData);

            // Call the backend API to create the category
            const result = await dispatch(createUserCategories({ data: categoryData })).unwrap();

            if (result) {
                // Refresh categories from backend to include the new one
                await dispatch(getTypeCategories({ id: formData.serviceLocation || TypeEnum.Home_Service }));

                // Reset form and close dialog
                setNewCategory("");
                setNewSubcategories("");
                setIsDialogOpen(false);

                // Show success message
                GlobalService.showToast("success", t("toast.titles.success"), t("postJob.categoryAddedSuccessfully"));
            }

        } catch (error) {
            console.error("Error adding category:", error);
            GlobalService.showToast("error", "Error", t("postJob.failedToAddCategory"));
        } finally {
            setIsAddingCategory(false);
        }
    };


    // Function to mask private information for WhatsApp messages
    const maskPrivateInfo = (info: any, type: string) => {
        if (!info || typeof info !== "string" || info.trim() === "") {
            return "N/A";
        }

        switch (type) {
            case "name":
                return info.replace(/(?!^.).(?!$)/g, "*");
            case "email": {
                if (!info.includes("@")) return "N/A";
                const [localPart, domain] = info.split("@");
                if (!localPart || !domain || !domain.includes(".")) return "N/A";
                const domainParts = domain.split(".");
                return `${localPart[0]}***@${domainParts[0][0]}***.${domainParts[1] || "com"}`;
            }
            case "phone":
                return info.replace(/\d(?=\d{4})/g, "*");
            case "address":
                return info.replace(/\d+|[a-zA-Z]+(?= )/g, "***");
            default:
                return info;
        }
    };

    // Function to auto-assign emergency professionals
    const autoAssignEmergencyProfessionals = async (createdJob: any) => {
        try {
            console.log("Auto-assigning emergency professionals for job:", createdJob.id);

            // Fetch emergency professionals matching the service area and category
            const serviceAreaId = createdJob.serviceArea?.id || collectedFormData?.serviceArea?.id;
            const categoryId = createdJob.categoryId?.id || collectedFormData?.category || formData?.category;
            
            if (!serviceAreaId) {
                console.log("No service area specified, skipping auto-assignment");
                return;
            }

            const result = await dispatch(
                getEmergencyProfessionals({
                    page: 1,
                    limit: 50, // Get all matching professionals
                    serviceAreaId: String(serviceAreaId),
                    categoryId: categoryId ? String(categoryId) : undefined,
                    sortBy: "createdAt",
                    sortOrder: "desc",
                    isShowLoader: false,
                })
            );

            if (getEmergencyProfessionals.fulfilled.match(result)) {
                const professionals = result.payload?.data || [];
                console.log(`Found ${professionals.length} matching emergency professionals`);

                // Filter professionals with WhatsApp numbers
                const professionalsWithWhatsApp = professionals.filter((p: any) => p.whatsappNumber);
                console.log(`${professionalsWithWhatsApp.length} professionals have WhatsApp`);

                // Assign each professional
                for (const professional of professionalsWithWhatsApp) {
                    try {
                        // 1. Send WhatsApp notification (encrypted client info)
                        if (professional.whatsappNumber) {
                            try {
                                const whatsAppData = {
                                    to: professional.whatsappNumber,
                                    var1_name: `${professional.firstName} ${professional.lastName}`,
                                    var2_jobTitle: createdJob.title || collectedFormData?.title || t("postJob.emergencyJob"),
                                    var3_jobDescription: (createdJob.description || collectedFormData?.description || t("postJob.emergencyServiceRequest")).replace(/\s*\n\s*/g, " ").trim(),
                                    var4_clientName: maskPrivateInfo(createdJob.contactName || collectedFormData?.contactName || user?.firstName + " " + user?.lastName, "name"),
                                    var5_clientPhone: maskPrivateInfo(createdJob.contactPhone || collectedFormData?.contactPhone, "phone"),
                                    var6_clientWhatsapp: maskPrivateInfo(createdJob.contactWhatsapp || collectedFormData?.contactWhatsapp, "phone"),
                                    var7_clientEmail: maskPrivateInfo(createdJob.contactEmail || collectedFormData?.contactEmail || user?.email, "email"),
                                    var8_clientAddress: maskPrivateInfo(createdJob.address || collectedFormData?.address, "address"),
                                    var9_detailsUrl: "https://kokkobeo.com/myproposals"
                                };

                                await dispatch(assignProfessionalWhatsApp(whatsAppData));
                            } catch (whatsappError) {
                                console.warn(`WhatsApp notification failed for ${professional.firstName}:`, whatsappError);
                            }
                        }

                        // 2. Create quote in database
                        const quoteData = {
                            status: { id: QuoteStatusEnum.Pending },
                            amount: null,
                            purchaseType: { id: PurchaseQuoteTypeEnum.Assign },
                            escrow: null,
                            job: { id: createdJob.id },
                            user: { id: professional.id }
                        };

                        const quote = await createQuote(quoteData);

                        // 3. Create notification for the professional
                        if (quote) {
                            try {
                                const notificationData = {
                                    title: t("postJob.newEmergencyServiceAssignment"),
                                    message: t("postJob.assignedToEmergencyRequest", { jobTitle: createdJob.title || t("postJob.emergencyJob") }),
                                    url: "/myproposals",
                                    status: { id: NotificationStatusEnum.Unread },
                                    priority: { id: NotificationPriorityEnum.High },
                                    type: { id: NotificationTypeEnum["In-App"] },
                                    user: { id: professional.id }
                                };

                                await createUserNotificationAction(notificationData);
                            } catch (notificationError) {
                                console.warn(`Notification failed for ${professional.firstName}:`, notificationError);
                            }
                        }
                    } catch (error) {
                        console.error(`Failed to assign ${professional.firstName}:`, error);
                    }
                }

                console.log(`Successfully auto-assigned ${professionalsWithWhatsApp.length} professionals`);
            }
        } catch (error) {
            console.error("Error in auto-assignment:", error);
            // Don't fail the job creation if auto-assignment fails
        }
    };

    const handlePlumberFormSubmit = (plumberFormData: any) => {
        console.log("Plumber form data collected:", plumberFormData);
        console.log("Parent form data:", formData);

        // Store the collected form data
        setCollectedFormData(plumberFormData);

        // Show success message for data collection
        GlobalService.showToast("success", t("toast.titles.success"), t("postJob.formDataCollectedSuccessfully"));
    };

    const handleLawyerFormSubmit = (lawyerFormData: any) => {
        console.log("Lawyer form data collected:", lawyerFormData);
        console.log("Parent form data:", formData);

        // Store the collected form data
        setCollectedFormData(lawyerFormData);

        // Show success message for data collection
        GlobalService.showToast("success", t("toast.titles.success"), t("postJob.formDataCollectedSuccessfully"));
    };

    // Check if user can proceed with action (profile + verification)
    // Check if user needs to complete profile
    const checkProfileCompletion = () => {
        if (user && !user.phone) {
            setShowProfileCompletionDialog(true);
            return false; // Profile incomplete
        }
        return true; // Profile complete
    };

    // Track when verification data has loaded
    useEffect(() => {
        if (verificationData !== null && verificationData !== undefined) {
            setVerificationDataLoaded(true);
        }
    }, [verificationData]);

    // Check if user needs verification
    const checkVerification = () => {
        // Don't check verification if data hasn't loaded yet
        if (!verificationDataLoaded) {
            return true; // Allow action to proceed while loading
        }
        
        if (!isVerified()) {
            setShowVerificationDialog(true);
            return false; // Verification incomplete
        }
        return true; // Verification complete
    };

    // Check both profile and verification
    const checkRequirements = () => {
        // Don't block actions if verification data is still loading
        if (!verificationDataLoaded) {
            // Only check profile completion while verification loads
            if (!checkProfileCompletion()) {
                return false;
            }
            return true;
        }
        
        // Check verification first (higher priority)
        // if (!checkVerification()) {
        //     return false;
        // }
        
        // Then check profile completion
        if (!checkProfileCompletion()) {
            return false;
        }
        
        return true;
    };

    // Helper function to navigate to profile based on user role
    const navigateToProfile = () => {
        if (!user?.role?.id) return;
        
        const roleId = Number(user.role.id);
        if (roleId === RoleEnum.professional || roleId === RoleEnum.internationl_professional) {
            navigate("/myprofile");
        } else if (roleId === RoleEnum.client || roleId === RoleEnum.internationl_client) {
            navigate("/client/profile");
        } else if (roleId === RoleEnum.admin) {
            navigate("/admin/dashboard");
        }
        setShowProfileCompletionDialog(false);
    };

    // Helper function to handle step navigation with profile completion check
    const handleStepNavigation = (nextStep: number) => {
        // Check if user needs to complete profile and verification
        if (!checkRequirements()) {
            return;
        }
        setStep(nextStep);
    };

    // Common function to check and trigger auto-assignment
    const checkAndAutoAssign = async (job: any, payload: any) => {
        const isEmergency = payload.isEmergency;
        const deadlineTypeId = Number(collectedFormData?.deadlineType?.id);
        
        if (isEmergency && deadlineTypeId === 5) {
            console.log("🚨 Emergency job detected - auto-assigning professionals");
            // Auto-assign in background (don't wait for completion)
            autoAssignEmergencyProfessionals(job).catch(err => {
                console.error("Auto-assignment failed:", err);
            });
        }
    };

    const handleJobCreation = async () => {
        // Check if user needs to complete profile and verification
        if (!checkRequirements()) {
            return;
        }

        if (!collectedFormData) {
            GlobalService.showToast("error", "Error", t("postJob.pleaseFillOutFormFirst"));
            return;
        }

        try {
            console.log("Creating job with collected data:", collectedFormData);
            console.log("Parent form data:", formData);
            console.log("User ID:", user?.id);

            // Check if selected category requires verification
            const selectedCategory = typeCategories.find(cat => cat.id === formData.category);
            const requiresVerification = selectedCategory?.isRequireVerification === true;
            
            // Determine job status based on category verification requirement
            let jobStatus;
            if (isEditMode) {
                // For edit mode, keep existing status
                jobStatus = Number(currentJob?.status?.id) || JobStatusEnum.Approved;
            } else {
                // For new jobs, check if verification is required
                jobStatus = requiresVerification ? JobStatusEnum.Pending : JobStatusEnum.Approved;
            }

            console.log("Category requires verification:", requiresVerification);
            console.log("Setting job status to:", jobStatus);

            // Calculate quote price based on QuotePricing rules
            let calculatedAmount = 0;
            if (formData.requestType === "quote") {
                const jobDataForPricing = {
                    categoryId: formData.category,
                    province: collectedFormData.province,
                    municipality: collectedFormData.municipality,
                    city: collectedFormData.city,
                    serviceArea: collectedFormData.serviceArea,
                    isEmergency: formData.urgency === "emergency",
                    isQuote: true
                };

                console.log("Calculating quote price for job:", jobDataForPricing);

                const priceResult = calculateQuotePrice(quotePricings, jobDataForPricing, 20);
                calculatedAmount = priceResult.price;

                console.log("Quote price calculation result:", {
                    price: priceResult.price,
                    source: priceResult.source,
                    matchedRegion: priceResult.matchedRegion?.region?.name
                });
            }

            // Combine the form data with the parent form data
            const combinedPayload = {
                ...collectedFormData,
                id: isEditMode ? currentJob?.id : "",
                userId: {
                    id: isEditMode ? currentJob?.userId?.id : user?.id,
                },
                status: { id: jobStatus },
                categoryId: { id: formData.category || "" },
                subCategories: formData.selectedSubcategories, // Keep subcategories from previous step
                isQuote: formData.requestType === "quote",
                isEmergency: formData.urgency === "emergency",
                isLocal: jobType != "international",

                // Common fields that might be in both plumber and lawyer forms
                title: collectedFormData.title || "",
                description: collectedFormData.description || "",
                budget: Number(collectedFormData.budget) || 0,
                files: collectedFormData.files || [],
                
                // Add calculated amount for quote jobs
                ...(formData.requestType === "quote" && calculatedAmount > 0 ? {
                    amount: calculatedAmount
                } : {}),

                // Handle currency and budgetType based on form type
                ...(formData.requestType === "quote" ? {
                    // Plumber form structure
                    currency: collectedFormData.currency || "USD",
                    budgetType: collectedFormData.budgetType || "fixed",
                    contactName: collectedFormData.contactName,
                    contactPhone: collectedFormData.contactPhone,
                    contactEmail: collectedFormData.contactEmail,
                    canContactByWhatsapp: collectedFormData.canContactByWhatsapp,
                    contactWhatsapp: collectedFormData.contactWhatsapp,
                    deadlineType: collectedFormData.deadlineType,
                    emergencyDays: collectedFormData.emergencyDays,
                    emergencyWhatsappNumber: collectedFormData.emergencyWhatsappNumber,
                    emergencyStartTime: collectedFormData.emergencyStartTime,
                    emergencyEndTime: collectedFormData.emergencyEndTime,
                    serviceArea: collectedFormData.serviceArea,
                    propertyType: collectedFormData.propertyType,
                    address: collectedFormData.address,
                    street: collectedFormData.street,
                    city: collectedFormData.city,
                    province: collectedFormData.province,
                    postalcode: collectedFormData.postalcode,
                    floor: Number(collectedFormData.floor) || 0,
                    isStairsRequired: collectedFormData.isStairsRequired,
                    isElevatorAvailable: collectedFormData.isElevatorAvailable,
                    serviceType: collectedFormData.serviceType,
                } : {
                    // Lawyer form structure
                    currency: collectedFormData.currency || { id: "USD" },
                    budgetType: collectedFormData.budgetType || { id: 1 },
                    languages: collectedFormData.languages || [],
                    requiredExperience: collectedFormData.requiredExperience || null,
                    durationTimeline: collectedFormData.durationTimeline || null,
                    // Contact information for lawyer form
                    contactName: collectedFormData.contactName,
                    contactPhone: collectedFormData.contactPhone,
                    contactEmail: collectedFormData.contactEmail,
                    canContactByWhatsapp: collectedFormData.canContactByWhatsapp,
                    contactWhatsapp: collectedFormData.contactWhatsapp,
                }),
            };

            console.log("Combined payload to be sent:", combinedPayload);

            let result;
            if (isEditMode && jobId) {
                // Update existing job
                result = await dispatch(updateUserJob({ id: jobId, data: combinedPayload }));
                console.log("API response for update:", result);

                if (updateUserJob.fulfilled.match(result)) {
                    console.log("✅ Job updated successfully!");
                    const updatedJob = result.payload;

                    // Auto-assign emergency professionals if job is emergency (both new and updated jobs)
                    // await checkAndAutoAssign(updatedJob, combinedPayload);

                    GlobalService.showToast("success", t("toast.titles.success"), t("postJob.jobUpdatedSuccessfullyToast"));

                    // Call the callback if provided (for modal mode), otherwise navigate
                    if (onJobUpdated) {
                        onJobUpdated();
                    } else {
                        // Redirect to My Orders page instead of showing thank you page
                        navigate("/client/myorders");
                    }
                } else {
                    console.error("❌ Job update failed:", result.error);
                    GlobalService.showToast("error", "Error", t("postJob.failedToUpdateJob"));
                }
            } else {
                // Create new job
                result = await dispatch(createUserJobs({ data: combinedPayload }));
                console.log("API response for creation:", result);

                if (createUserJobs.fulfilled.match(result)) {
                    console.log("✅ Job created successfully!");
                    const createdJob = result.payload;

                    // Auto-assign emergency professionals if job is emergency (both new and updated jobs)
                    await checkAndAutoAssign(createdJob, combinedPayload);

                    GlobalService.showToast("success", t("toast.titles.success"), t("postJob.jobRequestSubmittedSuccessfully"));

                    // Only show thank you page for new jobs, not for edits
                    if (!onJobUpdated) {
                        setShowThankYou(true);

                        // Save the tab preference and redirect to MyOrders page after 3 seconds
                        localStorage.setItem("myOrdersSelectedTab", "requests");
                        setTimeout(() => {
                            navigate("/client/myorders?isJobCreated=true");
                        }, 3000);
                    }
                } else {
                    console.error("❌ Job creation failed:", result.error);
                    GlobalService.showToast("error", "Error", t("postJob.failedToSubmitJobRequest"));
                }
            }
        } catch (error) {
            console.error("Error submitting plumber form:", error);
        }
    };

    const renderStep1 = () => (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold">{t("postJob.whatWouldYouLikeToDo")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                <Card
                    color={undefined}
                    className={`p-6 cursor-pointer transition-all ${formData.requestType === "quote" ? "ring-2 ring-blue-500 bg-blue-50" : "hover:bg-gray-50"
                        }`}
                    onClick={() => {
                        setShowQuoteModal(true);
                    }}>
                    <div className="space-y-3">
                        <DollarSign className="h-8 w-8 text-blue-600" />
                        <h3 className="font-semibold text-lg">{t("postJob.getQuotes")}</h3>
                        <p className="text-sm text-gray-600">{t("postJob.getQuotesDescription")}</p>
                    </div>
                </Card>

                <Card
                    color={undefined}
                    className={`p-6 cursor-pointer transition-all ${formData.requestType === "hire" ? "ring-2 ring-blue-500 bg-blue-50" : "hover:bg-gray-50"
                        }`}
                    onClick={() => {
                        setFormData({ ...formData, requestType: "hire" });
                        // Skip step 2 for international jobs, go directly to step 3
                        if (jobType === "international") {
                            setStep(3);
                        } else {
                            setStep(2);
                        }
                    }}>
                    <div className="space-y-3">
                        <Briefcase className="h-8 w-8 text-blue-600" />
                        <h3 className="font-semibold text-lg">{t("postJob.hireProfessional")}</h3>
                        <p className="text-sm text-gray-600">{t("postJob.hireProfessionalDescription")}</p>
                        <p className="text-sm text-red-600">{t("postJob.setFixedOrHourlyPrice")}</p>
                    </div>
                </Card>
            </div>
            {!onJobUpdated && <div className="flex justify-center sm:justify-start mb-8">
                <Button
                    variant="outline"
                    onClick={() => navigate("/clients")}
                    className="px-4 sm:px-6 py-2 text-sm sm:text-base">
                    {t("postJob.backToDashboard")}
                </Button>
            </div>}
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <h2 className="text-xl font-semibold">{t("postJob.whereDoYouNeedService")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
                <Card
                    color={undefined}
                    className={`p-6 cursor-pointer transition-all ${formData.serviceLocation === TypeEnum.Home_Service
                        ? "ring-2 ring-blue-500 bg-blue-50"
                        : "hover:bg-gray-50"
                        }`}
                    onClick={async () => {
                        // Check if user needs to complete profile and verification
                        if (!checkRequirements()) {
                            return;
                        }
                        await dispatch(getTypeCategories({ id: TypeEnum.Home_Service }));
                        setFormData({ ...formData, serviceLocation: TypeEnum.Home_Service });
                        setStep(3);
                    }}>
                    <div className="space-y-3">
                        <MapPin className="h-8 w-8 text-blue-600" />
                        <h3 className="font-semibold text-lg">{t("postJob.homeService")}</h3>
                        <p className="text-sm text-gray-600">{t("postJob.homeServiceDescription")}</p>
                    </div>
                </Card>

                <Card
                    color={undefined}
                    className={`p-6 cursor-pointer transition-all ${formData.serviceLocation === TypeEnum.Professional_Service
                        ? "ring-2 ring-blue-500 bg-blue-50"
                        : "hover:bg-gray-50"
                        }`}
                    onClick={async () => {
                        // Check if user needs to complete profile and verification
                        if (!checkRequirements()) {
                            return;
                        }
                        await dispatch(getTypeCategories({ id: TypeEnum.Professional_Service }));
                        setFormData({ ...formData, serviceLocation: TypeEnum.Professional_Service });
                        setStep(3);
                    }}>
                    <div className="space-y-3">
                        <Globe className="h-8 w-8 text-blue-600" />
                        <h3 className="font-semibold text-lg">{t("postJob.professionalService")}</h3>
                        <p className="text-sm text-gray-600">{t("postJob.professionalServiceDescription")}</p>
                    </div>
                </Card>
            </div>
            <div className="flex justify-center sm:justify-start mb-6">
                <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="px-4 sm:px-6 py-2 text-sm sm:text-base">
                    {t("postJob.back")}
                </Button>
            </div>
        </div>
    );

    const renderStep3 = () => {
        const selectedCategory = typeCategories.find(cat => cat.id === formData.category);

        return (
            <div className="space-y-4 sm:space-y-6 mb-8 mb-16">
                <h2 className="text-xl font-semibold">{t("postJob.serviceTypeAndUrgency")}</h2>

                {/* Category Selection */}
                <div className="p-1">
                    <label className="block text-sm font-medium mb-2">{t("postJob.selectCategory")}</label>

                    {/* Search Bar */}
                    <div className="mb-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder={t("postJob.searchCategories")}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        </div>
                        {searchTerm.length > 0 && searchTerm.length < 3 && (
                            <p className="text-sm text-amber-600 mt-1">
                                Please enter at least 3 characters to search
                            </p>
                        )}
                        {searchTerm.length >= 3 && filteredCategories.length > 0 && (
                            <p className="text-sm text-gray-600 mt-1">
                                Found {filteredCategories.length} category{filteredCategories.length !== 1 ? "ies" : "y"} matching &quot;{searchTerm}&quot;
                            </p>
                        )}
                    </div>

                    {/* Categories Grid */}
                    {searchTerm.trim() === "" || searchTerm.length < 3 ? (
                        <div className="text-center py-8 text-gray-500">
                            <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                            <p>{t("postJob.startTypingToSearch")}</p>
                        </div>
                    ) : filteredCategories.length === 0 ? (
                        <div className="text-center py-6">
                            <div className="text-gray-500 mb-4">
                                {t("postJob.noCategoriesFound", { searchTerm })}
                            </div>
                            <Button
                                onClick={() => setIsDialogOpen(true)}
                                className="inline-flex items-center gap-2">
                                <Plus className="h-4 w-4" />
                                {t("postJob.addNewCategoryButton")}
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {filteredCategories.map(category => (
                                    <Card
                                        color={undefined}
                                        key={category.id}
                                        className={`p-4 cursor-pointer transition-all ${formData.category === category.id
                                            ? "ring-2 ring-blue-500 bg-blue-50"
                                            : "hover:bg-gray-50"
                                            }`}
                                        onClick={() => {
                                            setFormData({ ...formData, category: category.id, selectedSubcategories: [] });
                                            setSubcategoryDisplayLimit(3); // Reset to show only 3 subcategories
                                        }}>
                                        <div className="flex items-center gap-3">
                                            <TextSelectionIcon className="h-5 w-5 text-blue-600" />
                                            <span className="text-sm font-medium">
                                                {getTranslatedCategoryName(category, currentLang) || t("postJob.unnamedCategory")}
                                            </span>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Subcategories Selection - Only show if category is selected and has subcategories */}
                {selectedCategory && selectedCategory.subcategories && selectedCategory.subcategories.length > 0 && (() => {
                    const originalSubcats = selectedCategory.subcategories || [];
                    
                    // Get translations - handle both object and array formats
                    const translationsRaw = selectedCategory.translatedSubCategories?.[currentLang];
                    let translatedSubcatsMap: Record<string, string> = {};
                    
                    if (translationsRaw) {
                        if (Array.isArray(translationsRaw)) {
                            // Array format: ['Installazione', 'Riparazione', ...]
                            originalSubcats.forEach((original, index) => {
                                if (translationsRaw[index]) {
                                    translatedSubcatsMap[original] = translationsRaw[index];
                                }
                            });
                        } else if (typeof translationsRaw === "object") {
                            // Object format: {Installation: 'Installazione', Repair: 'Riparazione', ...}
                            translatedSubcatsMap = translationsRaw;
                        }
                    }
                    
                    return (
                    <div className="mt-6 p-1 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-4">
                            <label className="block text-sm font-medium text-gray-700">
                                {t("postJob.selectSubcategories")}
                                {formData.selectedSubcategories.length > 0 && (
                                    <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                                        {formData.selectedSubcategories.length} {t("postJob.selected")}
                                    </span>
                                )}
                            </label>
                            {formData.selectedSubcategories.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, selectedSubcategories: [] })}
                                    className="text-xs text-red-600 hover:text-red-800 underline">
                                    {t("postJob.clearAll")}
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            {originalSubcats.slice(0, subcategoryDisplayLimit).map((subcategory, sliceIndex) => {
                                // Get translated name from the map, fallback to original
                                const displayName = translatedSubcatsMap[subcategory] || subcategory;
                                return (
                                <Card
                                    color={undefined}
                                    key={sliceIndex}
                                    className={`p-3 cursor-pointer transition-all ${formData.selectedSubcategories.includes(subcategory)
                                        ? "ring-2 ring-blue-500 bg-blue-50"
                                        : "hover:bg-gray-50 border-gray-200"
                                        }`}
                                    onClick={() => {
                                        const isSelected = formData.selectedSubcategories.includes(subcategory);
                                        const newSelectedSubcategories = isSelected
                                            ? formData.selectedSubcategories.filter(sub => sub !== subcategory)
                                            : [...formData.selectedSubcategories, subcategory];
                                        setFormData({ ...formData, selectedSubcategories: newSelectedSubcategories });
                                    }}>
                                    <div className="flex items-center gap-2">
                                        <TextSelectionIcon className="h-4 w-4 text-blue-600" />
                                        <span className="text-sm font-medium">{displayName}</span>
                                    </div>
                                </Card>
                            );})}
                        </div>

                        {/* Read More Button */}
                        {selectedCategory.subcategories.length > 3 && subcategoryDisplayLimit < selectedCategory.subcategories.length && (
                            <div className="mt-4 text-center">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const nextLimit = Math.min(subcategoryDisplayLimit + 3, selectedCategory.subcategories.length);
                                        setSubcategoryDisplayLimit(nextLimit);
                                    }}
                                    className="px-6 py-2 text-sm"
                                >
                                    {t("postJob.showMore", { count: Math.min(3, selectedCategory.subcategories.length - subcategoryDisplayLimit) })}
                                </Button>
                            </div>
                        )}

                        {/* Show All Button when some are hidden */}
                        {selectedCategory.subcategories.length > 3 && subcategoryDisplayLimit < selectedCategory.subcategories.length && (
                            <div className="mt-2 text-center">
                                <Button
                                    variant="ghost"
                                    onClick={() => setSubcategoryDisplayLimit(selectedCategory.subcategories.length)}
                                    className="px-4 py-1 text-xs text-blue-600 hover:text-blue-800"
                                >
                                    {t("postJob.showAll", { count: selectedCategory.subcategories.length })}
                                </Button>
                            </div>
                        )}

                        {/* Collapse Button when all are shown */}
                        {selectedCategory.subcategories.length > 3 && subcategoryDisplayLimit === selectedCategory.subcategories.length && (
                            <div className="mt-2 text-center">
                                <Button
                                    variant="ghost"
                                    onClick={() => setSubcategoryDisplayLimit(3)}
                                    className="px-4 py-1 text-xs text-gray-600 hover:text-gray-800"
                                >
                                    {t("postJob.showLess")}
                                </Button>
                            </div>
                        )}
                    </div>
                );})()}

                {/* Urgency Selection */}
                {formData.serviceLocation === TypeEnum.Home_Service &&
                    <><div>
                        <label className="block text-sm font-medium mb-2">{t("postJob.serviceUrgency")}</label>
                        <div className="grid grid-cols-1 p-1 sm:grid-cols-2 gap-4">
                            <Card
                                color={undefined}
                                className={`p-4 cursor-pointer transition-all ${formData.urgency === "normal" ? "ring-2 ring-blue-500 bg-blue-50" : "hover:bg-gray-50"}`}
                                onClick={() => setFormData({ ...formData, urgency: "normal" })}>
                                <div className="flex items-center gap-3">
                                    <Clock className="h-5 w-5 text-blue-600" />
                                    <div>
                                        <p className="font-medium">{t("postJob.normalService")}</p>
                                        <p className="text-sm text-gray-600">{t("postJob.normalServiceDescription")}</p>
                                    </div>
                                </div>
                            </Card>

                            <Card
                                color={undefined}
                                className={`p-4 cursor-pointer transition-all ${formData.urgency === "emergency" ? "ring-2 ring-red-500 bg-red-50" : "hover:bg-gray-50"}`}
                                onClick={() => setFormData({ ...formData, urgency: "emergency" })}>
                                <div className="flex items-center gap-3">
                                    <Shield className="h-5 w-5 text-red-600" />
                                    <div>
                                        <p className="font-medium">{t("postJob.emergencyService")}</p>
                                        <p className="text-sm text-gray-600">{t("postJob.emergencyServiceDescription")}</p>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div></>
                }
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between p-1 !mb-32">
                    <Button
                        variant="outline"
                        onClick={() => {
                            // For international jobs, go back to step 1, otherwise go back to step 2
                            if (jobType === "international") {
                                setStep(1);
                            } else {
                                setStep(2);
                            }
                        }}
                        className="px-4 sm:px-6 py-2 text-sm sm:text-base order-2 sm:order-1">
                        {t("postJob.back")}
                    </Button>
                    {formData.category && (
                        <Button
                            onClick={() => handleStepNavigation(4)}
                            className="px-4 sm:px-6 py-2 text-sm sm:text-base order-1 sm:order-2">
                            {t("postJob.continue")}
                        </Button>
                    )}
                </div>

            </div>
        );
    };

    const renderStep4 = () => (
        console.log(formData, TypeEnum.Home_Service),
        <>
            {formData.serviceLocation != TypeEnum.Home_Service ? (
                <div className="space-y-4 sm:space-y-6 mb-4 !mb-32">
                    <LawyerRequestForm
                        formData={formData}
                        onSubmit={handleLawyerFormSubmit}
                        onBack={() => {
                            // For international jobs, go back to step 1, otherwise go back to step 3
                            if (jobType === "international") {
                                setStep(1);
                            } else {
                                setStep(3);
                            }
                        }}
                        isEditMode={isEditMode}
                        initialData={currentJob}
                    />
                </div>
            ) : (
                <div className="space-y-4 sm:space-y-6 mb-4 !mb-32">
                    <PlumberRequestForm
                        formData={formData}
                        onSubmit={handlePlumberFormSubmit}
                        onBack={() => {
                            // For international jobs, go back to step 1, otherwise go back to step 3
                            if (jobType === "international") {
                                setStep(1);
                            } else {
                                setStep(3);
                            }
                        }}
                        isEditMode={isEditMode}
                        initialData={currentJob}
                    />
                </div>

            )}
        </>
    );

    const renderThankYou = () => (
        <div className="text-center space-y-8 py-12 mb-16">
            <div className="space-y-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEditMode ? t("postJob.jobUpdatedSuccessfully") : t("postJob.thankYou")}
                </h1>
                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                    {isEditMode
                        ? t("postJob.jobUpdatedMessage")
                        : t("postJob.jobSubmittedMessage")
                    }
                </p>
            </div>

            <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">{t("postJob.whatHappensNext")}</h3>
                    <ul className="text-left text-blue-800 space-y-2">
                        <li className="flex items-start gap-2">
                            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                            <span>{t("postJob.reviewRequirements")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                            <span>{t("postJob.receiveQuotes")}</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></span>
                            <span>{t("postJob.supportAvailable")}</span>
                        </li>
                    </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                        onClick={handleBackToStep1}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-700"
                    >
                        {isEditMode ? t("postJob.editAnotherJob") : t("postJob.postAnotherJob")}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => navigate("/client/myorders")}
                        className="px-8 py-3"
                    >
                        {t("postJob.viewYourJobs")}
                    </Button>
                </div>
            </div>
        </div>
    );
    console.log(formData);

    const renderStepIndicator = () => {
        if (showThankYou) return null; // Don't show step indicator on thank you page

        // For international jobs, we only have 3 steps (skip step 2)
        const totalSteps = jobType === "international" ? 3 : 4;
        const currentStep = jobType === "international" && step > 2 ? step - 1 : step;

        return (
            <div className="mb-6 sm:mb-8">
                {/* Page Title */}
                <div className="text-center mb-4 sm:mb-6">
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                        {isEditMode ? t("postJob.editJob") : t("postJob.postAJob")}
                    </h1>
                    <p className="text-sm sm:text-base text-gray-600">
                        {isEditMode ? t("postJob.updateJobDetails") : t("postJob.createNewJobRequest")}
                    </p>
                </div>

                {/* Mobile Layout */}
                <div className="block sm:hidden">
                    {/* Back Button - Top */}
                    {currentStep > 1 && (
                        <div className="mb-4">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    if (jobType === "international") {
                                        if (currentStep === 2) setStep(1);
                                        else if (currentStep === 3) setStep(3);
                                    } else {
                                        if (step === 2) setStep(1);
                                        else if (step === 3) setStep(2);
                                        else if (step === 4) setStep(3);
                                    }
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-sm"
                            >
                                ← Back
                            </Button>
                        </div>
                    )}

                    {/* Step Indicator - Centered */}
                    <div className="flex items-center justify-center mb-4">
                        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(stepNumber => (
                            <React.Fragment key={stepNumber}>
                                <div
                                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${stepNumber === currentStep
                                        ? "bg-blue-600 text-white"
                                        : stepNumber < currentStep
                                            ? "bg-green-100 text-green-600"
                                            : "bg-gray-100 text-gray-400"
                                        }`}>
                                    {stepNumber < currentStep ? "✓" : stepNumber}
                                </div>
                                {stepNumber < totalSteps && (
                                    <div className={`w-8 sm:w-12 h-1 ${stepNumber < currentStep ? "bg-green-100" : "bg-gray-100"
                                        }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Step Label */}
                    <div className="text-center">
                                        <p className="text-sm font-medium text-gray-700">
                                            {t("postJob.stepOf", { current: currentStep, total: totalSteps })}
                                        </p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {currentStep === 1 && t("postJob.chooseServiceType")}
                                            {currentStep === 2 && (jobType === "international" ? t("postJob.serviceDetails") : t("postJob.selectLocation"))}
                                            {currentStep === 3 && (jobType === "international" ? t("postJob.jobInformation") : t("postJob.serviceDetails"))}
                                            {currentStep === 4 && t("postJob.jobInformation")}
                                        </p>
                    </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:flex items-center justify-between">
                    {/* Back Button */}
                    {currentStep > 1 && (
                        <Button
                            variant="outline"
                            onClick={() => {
                                if (jobType === "international") {
                                    if (currentStep === 2) setStep(1);
                                    else if (currentStep === 3) setStep(3);
                                } else {
                                    if (step === 2) setStep(1);
                                    else if (step === 3) setStep(2);
                                    else if (step === 4) setStep(3);
                                }
                            }}
                            className="flex items-center gap-2 px-4 py-2"
                        >
                            ← Back
                        </Button>
                    )}

                    {/* Step Indicator */}
                    <div className="flex items-center justify-center flex-1">
                        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(stepNumber => (
                            <React.Fragment key={stepNumber}>
                                <div
                                    className={`w-8 h-8 rounded-full flex items-center justify-center ${stepNumber === currentStep
                                        ? "bg-blue-600 text-white"
                                        : stepNumber < currentStep
                                            ? "bg-green-100 text-green-600"
                                            : "bg-gray-100 text-gray-400"
                                        }`}>
                                    {stepNumber < currentStep ? "✓" : stepNumber}
                                </div>
                                {stepNumber < totalSteps && (
                                    <div className={`w-12 h-1 ${stepNumber < currentStep ? "bg-green-100" : "bg-gray-100"
                                        }`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Spacer to balance the layout */}
                    {currentStep > 1 ? <div className="w-20"></div> : null}
                </div>
            </div>
        );
    };
    const StoreLinks = () => (
        <div className="flex justify-center gap-2 pt-6 px-4">
            <a href="https://play.google.com/store/apps/details?id=com.kokobeo" className="w-1/2">
                <img
                    src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
                    alt="Get it on Google Play"
                    className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
                    style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
                />
            </a>
            <a href="https://apps.apple.com/app/kokobeo" className="w-1/2">
                <img
                    src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
                    alt="Download on App Store"
                    className="w-full h-auto rounded-md hover:opacity-90 transition-opacity"
                    style={{ maxWidth: "160px", height: "50px", objectFit: "contain" }}
                />
            </a>
        </div>
    );

    const SocialIcons = () => (
        <SocialMediaLinks className="flex justify-center space-x-4 pt-4 px-0 pb-6" iconSize="h-4 w-4" />
    );
    return (
        <>
            {isLoading && (
                <div className="fixed left-[50%] top-[40%] z-[1000px]">
                    <Spinner color="green" />
                </div>
            )}
            <div
                className={`min-h-screen bg-gray-50 ${isLoading ? "opacity-25" : "opacity-100"}`}
                style={{ textAlign: "left" }}>
                {showSidebar && <div className="fixed top-0 inset-x-0 z-50 bg-white border-b h-16 w-full">
                        <SharedHeader5 />
                </div>}
                {/* {isLoading && (
                <div className="absolute top-[50%] left-[50%]">
                    <Spinner color="gray" />
                </div>
            )} */}
                <div className={`flex min-h-[calc(100vh-64px)]${showSidebar ? " pt-16" : ""}`}>
                    {showSidebar && <><aside className="hidden lg:block fixed left-0 top-16 bottom-0 w-64 bg-white border-r overflow-y-auto h-[calc(100vh-64px)]">
                        <nav className="p-4 space-y-1 h-full flex flex-col">
                            <div className="flex-1 overflow-y-auto">
                                {sidebarClientItems.map((item, index) => item.path === "/logout" ? (
                                    <button
                                        key={index}
                                        onClick={handleLogout}
                                        className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                        {item.icon}
                                        <span>{item.label}</span>
                                    </button>
                                ) : (
                                    <Link
                                        key={index}
                                        to={item.path}
                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                                            ? "text-blue-600 bg-blue-50 font-medium"
                                            : "text-gray-600 hover:bg-gray-50"} ${item.align === "center" ? "justify-center-important" : ""}`}>
                                        <span
                                            className="flex items-center gap-3"
                                            style={item.align === "center"
                                                ? { justifyContent: "center !important" }
                                                : { justifyContent: "flex-start" }}>
                                            {item.icon}
                                            {item.label}
                                        </span>
                                    </Link>
                                )
                                )}
                            </div>
                            <div className="mt-auto w-full">
                                <StoreLinks />
                                <SocialIcons />
                            </div>
                        </nav>
                    </aside><AnimatePresence>
                            {isMobileMenuOpen && (
                                <>
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden" />
                                    <motion.aside
                                        initial={{ x: "-100%" }}
                                        animate={{ x: 0 }}
                                        exit={{ x: "-100%" }}
                                        transition={{ type: "spring", bounce: 0, duration: 0.4 }}
                                        className="fixed inset-y-0 left-0 w-full max-w-xs bg-white shadow-xl z-50 lg:hidden overflow-y-auto h-[calc(100vh-64px)]">
                                        <div className="p-4 h-full flex flex-col">
                                            <div className="flex items-center justify-between mb-6">
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src="https://assests.netlify.app/assets/images/logo.png"
                                                        alt="Logo"
                                                        className="h-8 w-8" />
                                                    <span className="text-blue-600 text-lg font-bold">Kokobeo</span>
                                                </div>
                                                <button
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                                                    <CloseIcon className="h-6 w-6" />
                                                </button>
                                            </div>
                                            <nav className="space-y-1 flex-1 overflow-y-auto">
                                                {sidebarClientItems.map((item, index) => item.path === "/logout" ? (
                                                    <button
                                                        key={index}
                                                        onClick={handleLogout}
                                                        className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                                        {item.icon}
                                                        <span>{item.label}</span>
                                                    </button>
                                                ) : (
                                                    <Link
                                                        key={index}
                                                        to={item.path}
                                                        className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${location.pathname === item.path
                                                            ? "text-blue-600 bg-blue-50 font-medium"
                                                            : "text-gray-600 hover:bg-gray-50"} ${item.align === "center" ? "justify-center-important" : ""}`}
                                                        style={item.align === "center"
                                                            ? { justifyContent: "center !important" }
                                                            : { justifyContent: "flex-start" }}
                                                        onClick={() => setIsMobileMenuOpen(false)}>
                                                        <span className="flex items-center gap-3">
                                                            {item.icon}
                                                            {item.label}
                                                        </span>
                                                    </Link>
                                                )
                                                )}
                                            </nav>
                                        </div>
                                    </motion.aside>
                                </>
                            )}
                        </AnimatePresence></>}
                    <div className={`flex-1 w-full${showSidebar ? " lg:pl-64" : ""}`}>
                        <main className="p-3 sm:p-4 lg:p-8 w-full">
                            <div className="max-w-4xl mx-auto h-full">
                                {renderStepIndicator()}

                                <div ref={mainContentRef} className={`overflow-y-auto pr-1 sm:pr-2 ${formData.requestType === "quote" ? "h-[calc(100vh-200px)] sm:h-[calc(100vh-180px)] pb-6 sm:pb-8" : "min-h-[calc(100vh-200px)] sm:min-h-[calc(100vh-180px)]"}`}>
                                    {showThankYou ? (
                                        renderThankYou()
                                    ) : (
                                        <>
                                            {step === 1 && renderStep1()}
                                            {step === 2 && renderStep2()}
                                            {step === 3 && renderStep3()}
                                            {step === 4 && renderStep4()}
                                        </>
                                    )}
                                </div>
                            </div>
                        </main>
                    </div>
                </div>
                {showSidebar && <SharedFooter3 />}
            </div>

            {/* Add Category Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="mx-4 sm:mx-auto">
                    <DialogHeader className="flex justify-between items-center">
                        <DialogTitle>{t("postJob.addNewCategory")}</DialogTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 rounded-full"
                            onClick={() => setIsDialogOpen(false)}>
                            <CloseIcon className="h-4 w-4" />
                        </Button>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">{t("postJob.categoryName")}</label>
                            <Input
                                value={newCategory}
                                onChange={e => setNewCategory(e.target.value)}
                                placeholder={t("postJob.enterCategoryName")}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">{t("postJob.subcategoriesCommaSeparated")}</label>
                            <Input
                                value={newSubcategories}
                                onChange={e => setNewSubcategories(e.target.value)}
                                placeholder={t("postJob.subcategoriesExample")}
                                className="mt-1"
                            />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isAddingCategory}>
                                {t("postJob.cancel")}
                            </Button>
                            <Button onClick={handleAddCategory} disabled={!newCategory.trim() || isAddingCategory}>
                                {isAddingCategory ? t("postJob.adding") : t("postJob.addCategory")}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Important Information Modal */}
            <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
                <DialogContent className="mx-auto my-auto max-w-md w-[90%] bg-white rounded-lg p-6 shadow-xl">
                    <DialogHeader className="flex justify-between items-center mb-4">
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
                            <Shield className="h-5 w-5 text-orange-600" />
                            {t("postJob.importantInformation")}
                        </DialogTitle>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 rounded-full"
                            onClick={() => setShowQuoteModal(false)}>
                            <CloseIcon className="h-4 w-4" />
                        </Button>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Warning Message */}
                        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 sm:p-4">
                            <p className="text-red-700 text-center font-medium mb-3 text-sm sm:text-base">
                                {t("postJob.quoteWarning")}
                            </p>

                            <ul className="text-red-700 space-y-2 text-sm sm:text-base">
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 bg-red-700 rounded-full mt-2 flex-shrink-0"></span>
                                    <span>{t("postJob.youWillRequestQuote")}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 bg-red-700 rounded-full mt-2 flex-shrink-0"></span>
                                    <span>{t("postJob.youWillReceiveQuotes")}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 bg-red-700 rounded-full mt-2 flex-shrink-0"></span>
                                    <span>{t("postJob.youWillReachAgreement")}</span>
                                </li>
                            </ul>
                        </div>

                        {/* Note Section */}
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                            <p className="text-gray-900 font-medium mb-2">{t("postJob.pleaseNote")}</p>
                            <p className="text-gray-700 text-xs sm:text-sm">
                                {t("postJob.platformUnaware")}
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-3 pt-3">
                            <Button
                                onClick={async () => {
                                    // Check if user needs to complete profile and verification
                                    const canProceed = await checkProfileCompletion();
                                    if (!canProceed) {
                                        return;
                                    }
                                    setFormData({ ...formData, requestType: "quote" });
                                    // Skip step 2 for international jobs, go directly to step 3
                                    if (jobType === "international") {
                                        setStep(3);
                                    } else {
                                        setStep(2);
                                    }
                                    setShowQuoteModal(false);
                                }}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-3"
                            >
                                {t("postJob.acceptAndContinueQuote")}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={async () => {
                                    // Check if user needs to complete profile and verification
                                    const canProceed = await checkProfileCompletion();
                                    if (!canProceed) {
                                        return;
                                    }
                                    setFormData({ ...formData, requestType: "hire" });
                                    // Skip step 2 for international jobs, go directly to step 3
                                    if (jobType === "international") {
                                        setStep(3);
                                    } else {
                                        setStep(2);
                                    }
                                    setShowQuoteModal(false);
                                }}
                                className="w-full text-sm px-4 py-3"
                            >
                                {t("postJob.declineAndContinueHiring")}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Profile and Verification Dialog */}
            {/* Profile Completion Dialog */}
            <ProfileCompletionDialog
                isOpen={showProfileCompletionDialog}
                onClose={() => setShowProfileCompletionDialog(false)}
                onGoToProfile={navigateToProfile}
                userRole={user?.role?.id}
            />

            {/* Verification Requirement Dialog */}
            <VerificationRequirementDialog
                isOpen={showVerificationDialog}
                onClose={() => setShowVerificationDialog(false)}
                title="Verification Required"
                message="Please complete your account verification to post jobs."
            />
        </>
    );
};

export default PostJobPage;